<template>
  <div class="store-details-page">
    <div class="store-directory-container container">
      <div
        class="top-nav-controls"
        role="navigation"
        aria-label="Store navigation links"
      >
        <NuxtLink
          class="top-nav-control-url"
          to="/store-directory"
          aria-label="Go to Directory and Map page"
        >
          Directory & Map
          <FontAwesomeIcon icon="fas fa-angle-right" class="chevron-icon" />
        </NuxtLink>

        <NuxtLink
          class="top-nav-control-url"
          to="/shops"
          aria-label="Go to Shops page"
          >Shops
          <FontAwesomeIcon icon="fas fa-angle-right" class="chevron-icon" />
        </NuxtLink>

        <span>
          {{ currentPage?.name }}
        </span>
      </div>

      <h1 class="store-title">
        {{ currentPage?.name }}
      </h1>

      <div :class="['store-details-wrapper']">
        <div :class="['store-wrapper', { row: isDesktop }]">
          <div class="store-image-wrapper col-xl-8">
            <NuxtImg
              class="store-image"
              v-if="currentPage?.store_front_image_url + '?auto=format,compress&fm=webp'"
              :src="currentPage?.store_front_image_url"
              format="webp"
              :alt="`${currentPage?.name} store front`"
            />
            <NuxtImg
              v-else
              format="webp"
              src="/Images/logos/hpv_logo_block.jpg"
              :alt="store.name"
            />

            <p
              v-if="currentPage?.plain_text_description && isDesktop"
              class="store-desc"
              v-html="currentPage.description"
            >
            </p>
          </div>

          <div class="store-address col-xl-4">
            <h2 class="address-headings">Address</h2>
            <a
              :href="googleMapsLink"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open store location in Google Maps, opens in new tab"
            >
              <p class="store-info">
                <span v-if="currentPage?.unit">{{ currentPage?.unit }}</span>
                Highland Park Village
                <br />
                Dallas, TX 75205, USA
              </p>
            </a>
            <template v-if="currentPage?.phone">
              <h3 class="address-headings">Phone</h3>
              <a class="store-info" :href="currentPage?.phone" target="_blank">
                {{ currentPage?.phone }}
              </a>
            </template>

            <template v-if="currentPage?.location_information">
              <h4 class="address-headings">Manager</h4>
              <div
                class="store-info"
                v-html="currentPage?.location_information"
              ></div>
            </template>

            <template v-if="!currentPage?.free_form_hours && finalHours">
              <h5 class="address-headings">Hours</h5>

              <ul class="store-hours-wrapper">
                <li
                  v-for="entry in finalHours"
                  :key="entry.day"
                  :class="['store-hours', { holiday: entry.isHoliday }]"
                >
                  <span class="day">{{
                    useTruncate(
                      entry.isHoliday && entry.holidayName
                        ? entry.holidayName
                        : entry.day,
                      40
                    )
                  }}</span>
                  <span class="hours">{{ entry.hours }}</span>
                </li>
              </ul>
            </template>

            <template v-if="currentPage?.free_form_hours">
              <h5 class="address-headings">Hours</h5>
              <div
                class="store-hours-wrapper free-text"
                v-html="currentPage?.free_form_hours"
              ></div>
            </template>

            <template v-if="currentPage?.menu_url">
              <div class="store-url">
                <a
                  class="store-info url"
                  :href="currentPage?.menu_url"
                  target="_blank"
                  >View Menu</a
                >
              </div>
            </template>

            <template v-if="currentPage?.reservation_url">
              <div class="store-url">
                <a
                  class="store-info url"
                  :href="currentPage?.reservation_url"
                  target="_blank"
                  >Reserve a Table</a
                >
              </div>
            </template>
          </div>
        </div>

        <span class="thick-divider offset-plus"></span>

        <div :class="['store-map-desc-container', { row: isDesktop }]">
          <div class="store-info-wrapper col-xl-12">
            <div class="inner-wrapper">
              <Mapplic
                v-show="!!mapData"
                :mapData="mapData"
                :storeList="storeList"
                :categories="categories"
                :height="isDesktop ? 450 : 250"
                ref="map_ref"
                :showPin="true"
                :minimap="false"
                :deeplinking="false"
                :sidebar="false"
                :lightbox="false"
                :fullscreen="false"
                :hovertip="true"
                :landmark="landmarkId"
                :maxscale="5"
                :mousewheel="false"
                tooltiplabel="Go to Store Page"
                @updateMap="onMapReady"
                :search="false"
                :tooltip="{ thumb: true, desc: true, link: true }"
                :bindLocationOpened="true"
                :bindLocationClosed="true"
                :bindLevelSwitched="true"
                :bindPositionChanged="true"
              />
            </div>
            <NuxtLink
              class="full-map-cta"
              to="/store-directory"
              aria-label="View the full Directory map pdf"
              >VIEW FULL DIRECTORY MAP</NuxtLink
            >
          </div>

          <p
            v-if="currentPage?.plain_text_description && !isDesktop"
            role="region"
            aria-label="Store description"
            class="store-desc"
          >
            {{ currentPage.plain_text_description }}
          </p>
        </div>
      </div>
    </div>

    <div class="container px-0">
      <span class="container thick-divider"></span>
    </div>

    <div class="back-navigator container">
      <NuxtLink
        to="/store-directory"
        aria-label="Go back to the Store Directory page"
      >
        BACK TO STORE DIRECTORY
      </NuxtLink>
    </div>

    <section class="event-list-wrapper py-5 container">
      <div v-if="currentPageIndex === boundaryPageIndex" role="list">
        <div
          role="listitem"
          v-for="(event, index) in boundaryPageFutureEvents"
          :key="`${event.id}-future-${index}`"
          class="event-card"
        >
          <div class="event-date-column">
            <div class="event-date-main month">
              {{ dayjs(event?.start_date).format("MMM") }}
            </div>
            <div class="event-date-main date">
              {{ dayjs(event?.start_date).format("DD") }}
            </div>
          </div>

          <div class="event-info-column">
            <div class="event-info-text">
              <div
                class="event-daterange"
                :class="{ 'with-time': event?.show_times }"
                :aria-label="`Event from ${formatDate(
                  event.start_date
                )} to ${formatDate(event.end_date)}${
                  event.show_times ? ', times shown' : ''
                }`"
              >
                <span v-if="!event?.show_times">
                  {{ formatDate(event?.start_date) }} -
                  {{ formatDate(event?.end_date) }}
                </span>
                <span v-else>
                  {{ formatDate(event?.start_date, false) }} @
                  {{ formatTime(event?.start_date) }} -
                  {{ formatTime(event?.end_date) }}
                </span>
              </div>

              <NuxtLink
                class="event-url"
                :to="`/event/${event?.slug}`"
                :aria-label="`View details for event: ${event?.name}`"
              >
                <h3 class="event-title">{{ event?.name }}</h3>
              </NuxtLink>
              <div
                class="event-body"
                v-html="useTruncate(event?.description, 200)"
              ></div>
            </div>

            <div
              class="event-info-image"
              :style="{
                backgroundColor: imageBackgrounds[event.id] || '#f3f3f3',
              }"
            >
              <NuxtLink
                class="event-url"
                :to="`/event/${event?.slug}`"
                :aria-label="`View details for event: ${event?.name}`"
              >
                <NuxtImg
                  :id="`event-img-${index}`"
                  class="event-image"
                  :src="event?.image_url + '?auto=format,compress&fm=webp'"
                  :alt="
                    event?.image_url_alt ||
                    event?.image_url_alt_text ||
                    event?.name
                  "
                  format="webp"
                  v-if="event?.image_url"
                  crossorigin="anonymous"
                />
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>

      <div v-else role="list">
        <div
          role="listitem"
          v-for="(event, index) in currentPageEvents"
          :key="`${event.id}-${index}-${currentPageIndex}`"
          class="event-card"
        >
          <div class="event-date-column">
            <div class="event-date-main month">
              {{ dayjs(event?.start_date).format("MMM") }}
            </div>
            <div class="event-date-main date">
              {{ dayjs(event?.start_date).format("DD") }}
            </div>
          </div>

          <div class="event-info-column">
            <div class="event-info-text">
              <div
                class="event-daterange"
                :class="{ 'with-time': event?.show_times }"
                :aria-label="`Event from ${formatDate(
                  event.start_date
                )} to ${formatDate(event.end_date)}${
                  event.show_times ? ', times shown' : ''
                }`"
              >
                <span v-if="!event?.show_times">
                  {{ formatDate(event?.start_date) }} -
                  {{ formatDate(event?.end_date) }}
                </span>
                <span v-else>
                  {{ formatDate(event?.start_date, false) }} @
                  {{ formatTime(event?.start_date) }} -
                  {{ formatTime(event?.end_date) }}
                </span>
              </div>

              <NuxtLink
                class="event-url"
                :to="`/event/${event?.slug}`"
                :aria-label="`View details for event: ${event?.name}`"
              >
                <h3 class="event-title">{{ event?.name }}</h3>
              </NuxtLink>
              <div
                class="event-body"
                v-html="useTruncate(event?.description, 200)"
              ></div>
            </div>

            <div
              class="event-info-image"
              :style="{
                backgroundColor: imageBackgrounds[event.id] || '#f3f3f3',
              }"
            >
              <NuxtLink
                class="event-url"
                :to="`/event/${event?.slug}`"
                :aria-label="`View details for event: ${event?.name}`"
              >
                <NuxtImg
                  :id="`event-img-${index}`"
                  class="event-image"
                  :src="event?.image_url + '?auto=format,compress&fm=webp'"
                  :alt="
                    event?.image_url_alt ||
                    event?.image_url_alt_text ||
                    event?.name
                  "
                  format="webp"
                  v-if="event?.image_url"
                  crossorigin="anonymous"
                />
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>

      <div class="event-toggle-navigation add-border" v-if="currentPageEvents.length">
        <button
          :disabled="currentPageIndex === 0"
          :aria-disabled="currentPageIndex === 0 ? true : false"
          class="nav-button"
          @click="prevPage"
        >
           <FontAwesomeIcon icon="fas fa-angle-left" class="chevron-icon" />
          Previous Events
        </button>

        <button
          :disabled="currentPageIndex === pages.length - 1"
          @click="nextPage"
          :aria-disabled="currentPageIndex === pages.length - 1 ? true : false"
          class="nav-button"
        >
          Next Events
          <FontAwesomeIcon icon="fas fa-angle-right" class="chevron-icon" />
        </button>
      </div>
    </section>

    <section>
      <UrlGrid
        :border="false"
        :featured-tiles="[
          { name: 'Personal Shoppers', col: 4 },
          { name: 'Guest Services', col: 4 },
          { name: 'Dining', col: 4 },
        ]"
      />
    </section>
  </div>
</template>

<script setup>
import { useRoute, useRouter } from "vue-router";
import ColorThief from "colorthief";
import { ref, computed, watch, onMounted, nextTick, reactive } from "vue";
const { isMobile, isDesktop } = useMobileChecker();
import { useAsyncData } from "#app";
import { useMainStore } from "~/store";
import Mapplic from "~/components/Mapplic.vue";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import minMax from "dayjs/plugin/minMax";
import { useMapData } from '~/composables/useMapData'
import _isEmpty from 'lodash/isEmpty';

dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(minMax);

const route = useRoute();
const router = useRouter();
const store = useMainStore();

const slug = route.params.slug;
const mapRef = ref(null);
const mapInstance = ref(null);
const mapIsReady = ref(false);
const today = dayjs().startOf("day");
const pageSize = 4;
const boundaryPastCount = 0;
const boundaryFutureCount = 4;
const boundaryPageIndex = ref(0);
const imageBackgrounds = reactive({});
const currentPageIndex = ref(0);
const pages = ref([]);
const storeList = computed(() => store.processedStores);
const categories = computed(() => store.processedCategories);

// *** SEO *** //
const currentSEO = await useSeo(`/stores/${slug}`);
useHead(currentSEO.value);

/* ASYNC DATA */
const currentPage = await useCurrentPage(slug, "store");

const { mapData, isLoading, error } = await useMapData(currentPage);
const reactivePage = reactive(currentPage);
const { finalHours, todaysHours } = await usePropertyHours(currentPage);

if (!reactivePage || _isEmpty(reactivePage)) {
  router.push('/shops/'); 
}

// Format date helper
const formatDate = (date) => dayjs(date).format("MMM D");
const formatTime = (date) => dayjs(date).format("h:mm A");

function generateRecurringInstances(event) {
  if (!event?.is_recurring || !event.recurring_events?.[0]) return [];

  const { frequency, recurring_days } = event.recurring_events[0];
  if (!Array.isArray(recurring_days)) return [];

  const selectedDayIndexes = recurring_days
    .filter((day) => day.selected)
    .map((day) => day.index);

  const start = dayjs(event.start_date).startOf("day");
  const end = dayjs(event.end_date).endOf("day");
  const instances = [];
  
  if (frequency === "monthly") {
    let current = start.clone();
    
    while (current.isSameOrBefore(end)) {
      if (
        current.date() === start.date() &&
        selectedDayIndexes.includes(current.day())
      ) {
        instances.push({
          ...event,
          original_start_date: event.start_date,
          start_date: current.toISOString(),
          is_recurring_instance: true,
        });
      }
      current = current.add(1, "month");
    }
  } else {
    const stepDays = frequency === "fortnightly" ? 14 : 7;

    selectedDayIndexes.forEach((weekdayIndex) => {
      let current = start.clone();      
      while (current.day() !== weekdayIndex && current.isBefore(end)) {
        current = current.add(1, "day");
      }

      while (current.isSameOrBefore(end)) {
        instances.push({
          ...event,
          original_start_date: event.start_date,
          start_date: current.toISOString(),
          is_recurring_instance: true,
        });
        current = current.add(stepDays, "day");
        if (current.isAfter(end)) break;
      }
    });
  }
  return instances;
}

// Flatten all events with recurring expansions
const allEventsExpanded = computed(() => {
  let events = store.processedEvents
  events = events.filter(el => el.eventable.id === currentPage.id)

  const expanded = events.flatMap((event) => {
    const instances = event.is_recurring
      ? generateRecurringInstances(event)
      : [event]

    return instances.map((e) => {
      let linkedStore = null

      if (e.eventable_type?.toLowerCase() === 'store') {
        linkedStore = store.processedStores.find((s) => s.id === e.eventable_id) || null
      } else if (e.eventable_type?.toLowerCase() === 'property') {
        linkedStore = store.property || null
      }

      return { ...e, store: linkedStore }
    })
  })
  return expanded
})


// Separate past and future events, sorted ascending by start_date
const pastEvents = computed(() =>
  allEventsExpanded.value
    .filter((e) => dayjs(e.end_date).isBefore(today) || dayjs(e.start_date).isBefore(today) && dayjs(e.end_date).isAfter(today) )
    .sort(
      (a, b) => dayjs(a.start_date).valueOf() - dayjs(b.start_date).valueOf()
    )
);

const futureEvents = computed(() =>
  allEventsExpanded.value
    .filter((e) => !dayjs(e.start_date).isBefore(today))
    .sort(
      (a, b) => dayjs(a.start_date).valueOf() - dayjs(b.start_date).valueOf()
    )
);

const boundaryPageFutureEvents = computed(() => {
  if (currentPageIndex.value !== boundaryPageIndex.value) return [];
  return currentPageEvents.value
    .filter((e) => dayjs(e.start_date).isSameOrAfter(today))
    .sort(
      (a, b) => dayjs(a.start_date).valueOf() - dayjs(b.start_date).valueOf()
    );
});

const boundaryPagePastEvents = computed(() => {
  if (currentPageIndex.value !== boundaryPageIndex.value) return [];
  return currentPageEvents.value
    .filter((e) => dayjs(e.end_date).isBefore(today))
    .sort(
      (a, b) => dayjs(a.start_date).valueOf() - dayjs(b.start_date).valueOf()
    );
});

// Paginate helper
function paginate(events, direction) {
  const paged = [];
  for (let i = 0; i < events.length; i += pageSize) {
    paged.push(
      direction === "reverse"
        ? events.slice(i, i + pageSize).reverse()
        : events.slice(i, i + pageSize)
    );
  }

  return paged;
}

// Boundary page combining last past and first future events
const boundaryPage = computed(() => {
  // const lastPast = pastEvents.value.slice(-boundaryPastCount); //reinstate is they want past events appearing again
  const firstFuture = futureEvents.value.slice(0, boundaryFutureCount);
  // return [...lastPast, ...firstFuture]; //reinstate is they want past events appearing again
  return firstFuture;
});

function buildPages() {
  pages.value = [];

  const sortedPast = pastEvents.value;
  const sortedFuture = futureEvents.value;

  // Extract the last past events for the boundary
  const lastPastForBoundary = sortedPast.slice(-boundaryPastCount);
  const remainingPast = sortedPast
    .slice(0, Math.max(0, sortedPast.length - boundaryPastCount))
    .reverse();

  // Extract the first future events for the boundary
  const firstFutureForBoundary = sortedFuture.slice(0, boundaryFutureCount);
  const remainingFuture = sortedFuture.slice(boundaryFutureCount);

  // Paginate past and future events BEFORE combining with boundary
  const pastPages = paginate(remainingPast, "reverse") // reversing direction of order
    .filter((p) => p.length > 0)
    .reverse();
  const futurePages = paginate(remainingFuture);

  // Create the boundary page and ensure it's sorted correctly
  const boundaryPageEvents = [
    // ...lastPastForBoundary, // ucomment to reinstate latest past events on boundary page (also need to set boundaryFutureCount to 3 or 6)
    ...firstFutureForBoundary,
  ].sort(
    (a, b) => dayjs(a.start_date).valueOf() - dayjs(b.start_date).valueOf()
  );

  // Combine into final pages array
  pages.value = [...pastPages, boundaryPageEvents, ...futurePages];

  // Set current page to the boundary page
  boundaryPageIndex.value = pastPages.length;
  currentPageIndex.value = pastPages.length;
}
// Initialize pages on events change
buildPages();

const currentPageEvents = computed(
  () => pages.value[currentPageIndex.value] || []
);

onMounted(() => {
  nextTick(() => {
    applyColorThiefToImages();
  });
});

watch(
  () => currentPageEvents.value,
  () => {
    nextTick(() => {
      applyColorThiefToImages();
    });
  }
);

// GRABS THE DOMINANT COLOR FROM EACH IMAGE AND SETS BACKGROUND COLOUR
function applyColorThiefToImages() {
  const colorThief = new ColorThief();

  currentPageEvents.value.forEach((event, index) => {
    const img = document.querySelector(`#event-img-${index}`);

    const setColor = () => {
      try {
        const color = colorThief.getColor(img);
        const bgColor = isDarkColor(color)
          ? "rgba(220, 206, 190, 0.4)"
          : `rgba(${color[0]}, ${color[1]}, ${color[2]}, 0.4)`;

        imageBackgrounds[event.id] = bgColor;
      } catch (e) {
        console.warn("Color Thief failed:", e);
        imageBackgrounds[event.id] = "#dccebe";
      }
    };

    if (img && img.complete) {
      setColor();
    } else if (img) {
      img.addEventListener("load", setColor);
    }
  });
}

// CHECKS FOR DARK DOMINANT COLOR AND REDUCES BACKGROUND LUMINANCE
function isDarkColor([r, g, b]) {
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance < 60; // Adjust threshold if needed
}

function prevPage() {
  if (currentPageIndex.value > 0) {
    currentPageIndex.value--;
  }
}

function nextPage() {
  if (currentPageIndex.value < pages.value.length - 1) {
    currentPageIndex.value++;
  }
}

const { googleMapsLink } = useGoogleMapsLink(
  "Highland Park Village, Dallas, TX 75205, USA",
  currentPage?.unit
);

const onMapReady = (location) => {
  console.log("✅ onMapReady");

  nextTick(() => {
    if (mapRef.value && typeof mapRef.value.showLocation === "function") {
      dropPin(reactivePage);
    } else {
      console.error("❌ mapRef or showLocation not ready:", mapRef.value);
    }
  });
};

const landmarkId = computed(() => {
  if (!mapData.value || !currentPage) return null
  
  return mapData.value?.levels
    ?.flatMap((level) => level.locations)
    ?.find((loc) => loc.store_id === currentPage?.id)?.id;
});

const currentStoreId = computed(() => {
  return storeList.value?.find((s) => s.slug === slug)?.id;
});

const updateMap = (map) => {
  mapInstance.value = map;
  setTimeout(() => {
    if (reactivePage && mapInstance.value !== null) {
      dropPin(reactivePage);
    }
  }, 500);
};

const dropPin = (store) => {
  if (!store || !mapRef.value || !mapRef.value.showLocation) {
    return;
  }

  const locationId = mapData.value?.levels
    ?.flatMap((level) => level.locations)
    ?.find((loc) => loc.store_id === store.id)?.id;

  if (locationId) {
    mapRef.value.showLocation(locationId);
  } else {
    console.warn("⚠️ No locationId found for store:", store.id);
  }
};
</script>

<style lang="scss" scoped>
.top-nav-controls {
  margin: 10px 0 30px 0px;

  a {
    text-decoration: none;
    color: $black;
  }

  .top-nav-control-url,
  span {
    font-family: $font_poppin_bold;
    text-transform: uppercase;
    font-size: 10px;
    line-height: 10px;
    letter-spacing: 1px;
  }

  .chevron-icon {
    height: 6px;
    font-family: $font_didot_bold;
    vertical-align: middle;
    font-weight: 900;
    margin-right: 5px;
    stroke: currentColor;
    stroke-width: 80; // MAKES BOLDER
    width: 6px;
  }

  @media only screen and (max-width: 1024px) {
    padding-left: 20px;
    padding-right: 20px;
  }

  @media only screen and (max-width: 580px) {
    padding-left: 0px;
    padding-right: 0px;
  }
}

.store-details-page {
  margin-bottom: 50px;
  @media only screen and (max-width: 1024px) {
    padding-left: 20px;
    padding-right: 20px;
  }

  @media only screen and (max-width: 580px) {
    padding-left: 0px;
    padding-right: 0px;
  }
}

.back-navigator {
  padding-top: 9px;
  padding-left: 0;
  // border-top: 8px solid $black;
  a {
    font-family: $font_poppin_bold;
    font-size: 12px;
    line-height: 12px;
    font-weight: bold;
    text-transform: uppercase;
    text-decoration: underline;
    color: $black;
    letter-spacing: 1.5px;
  }

  @media only screen and (max-width: 1024px) {
    // padding-left: 20px;
    // padding-right: 20px;
    width: 94%;
  }

  @media only screen and (max-width: 580px) {
    padding-left: 0px;
    padding-right: 0px;
  }
}

.store-directory-container {
  // border-bottom: 8px solid $black;

  h1 {
    font-family: $font_poppin_bold;
    font-size: 30px;
    line-height: 30px;
    font-weight: bold;
    text-transform: uppercase;
    margin: 42px 0 20px 0;
    border-bottom: none;

    @media only screen and (max-width: 1024px) {
      padding-left: 20px;
      padding-right: 20px;
    }

    @media only screen and (max-width: 580px) {
      padding-left: 0px;
      padding-right: 0px;
    }
  }

  .store-wrapper {
    // border-bottom: 8px solid $black;
    padding-bottom: 15px;
  }
  .store-details-wrapper {
    .store-image-wrapper {
      // padding-left: 0;
      .store-image {
        width: 100%;
        height: auto;
      }

      .store-desc {
        font-family: $font_didot_ltd_roman;
        font-size: 18px;
        line-height: 22px;
        margin-top: 56px;
        -moz-box-sizing: border-box;
        -webkit-box-sizing: border-box;
        box-sizing: border-box;
      }

      @media only screen and (max-width: 580px) {
        padding: 0;
      }
    }

    .store-address {
      border-left: 1px solid $black;
      padding: 0px 0 25px 25px;

      a {
        text-decoration: none;
        color: $black;
      }

      .address-headings {
        font-family: $font_poppin_bold;
        font-size: 14px;
        line-height: 22px;
        font-weight: bold;
        text-transform: uppercase;
        letter-spacing: 1px;
        margin-bottom: 0rem;

        &:not(:first-child) {
          margin-top: 30px;
        }
      }

      .store-info {
        font-family: $font_poppin_light;
        font-size: 12px;
        line-height: 20px;
        letter-spacing: 1.5px;
        text-transform: uppercase;
        text-decoration: none;
        color: $black !important;
      }

      .store-url {
        margin: 10px 0;

        .url {
          font-family: $font_poppin_bold;
          text-decoration: underline;
          color: $black !important;
        }
      }

      .store-hours-wrapper {
        list-style: none;
        padding: 0;
        padding-bottom: 15px;

        &.free-text {
          width: 70%;
          font-family: $font_poppin_light;
          font-size: 12px;
          line-height: 20px;
          letter-spacing: 1.5px;
          text-transform: uppercase;

          :deep(strong) {
            font-family: $font_poppin_bold;
          }
        }

        .store-hours {
          display: grid;
          grid-template-columns: 1fr auto;
          column-gap: 1rem;
          row-gap: 0.25rem;
          justify-items: start;
          align-items: center;
          max-width: 300px;
          width: 100%;
          font-family: $font_poppin_light;
          font-size: 12px;
          line-height: 20px;
          letter-spacing: 1.5px;
          text-transform: uppercase;

          .day {
            min-width: 100px;
          }

          .hours {
            min-width: 120px;
          }
        }
      }

      @media only screen and (max-width: 1024px) {
        padding: 0px 0 0 25px;
        border: none;
        margin-top: 40px;

        .store-hours-wrapper {
          padding-bottom: 5px;
        }
      }

      @media only screen and (max-width: 992px) {
        padding: 0px 0 0 15px;
        .store-hours-wrapper {
          margin-bottom: 0px;
        }
      }

      @media only screen and (max-width: 580px) {
        padding: 0px 0 0 0px;
        .store-hours-wrapper {
          margin-bottom: 0px;
        }
      }
    }

    .store-map-desc-container {
      .store-info-wrapper {
        padding: 0px;
        .inner-wrapper {
          margin: 40px 0px 20px;
          padding: 20px 20px;
          border: 1px solid #d9d9d9;
        }
      }

      .full-map-cta {
        font-family: $font_poppin_bold;
        display: flex;
        justify-content: center;
        margin-bottom: 20px;
        font-size: 12px;
        line-height: 12px;
        color: $black;
        text-decoration: none;
        font-weight: bold;
        text-transform: uppercase;
        letter-spacing: 1.5px;
      }

      @media only screen and (max-width: 1024px) {
        .store-info-wrapper {
          padding: 0px;
          .inner-wrapper {
            margin: 40px 0px 20px;
            padding: 0 5px;
            border: 1px solid #d9d9d9;
          }
        }

        .store-desc {
          padding: 30px 0;
          border-top: 1px solid $black;
          font-family: $font_didot_ltd_roman;
          font-size: 18px;
          line-height: 22px;
          letter-spacing: -.25px;
        }

        @media only screen and (max-width: 1024px) {
          padding-left: 20px;
          padding-right: 20px;
        }

        @media only screen and (max-width: 992px) {
          padding-left: 15px;
          padding-right: 15px;
        }

        @media only screen and (max-width: 580px) {
          padding-left: 0px;
          padding-right: 0px;
        }
      }
    }
  }
}

.event-list-wrapper {
  margin-top: 10px;
  @media only screen and (max-width: 1024px) {
    padding-left: 50px;
    padding-right: 50px;
  }

  @media only screen and (max-width: 580px) {
    padding-left: 20px;
    padding-right: 20px;
  }
}

.event-item {
  margin-bottom: 1rem;
  font-size: 1rem;
}

.btn {
  cursor: pointer;
  padding: 0.3rem 0.7rem;
  font-size: 1.2rem;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.btn:hover {
  background: #e5e7eb;
}

.boundary-header {
  font-family: $font_poppin_bold;
  font-size: 28px;
  line-height: 1.42;
  letter-spacing: 2px;

  @media only screen and (max-width: 992px) {
    margin: 30px auto;
  }
}

.event-card {
  display: flex;
  gap: 2rem;
  padding: 1.5rem 0;
  align-items: flex-start;

  @media only screen and (max-width: 992px) {
    gap: 0.5rem;
    padding: 0.75rem 0;
  }
}

.event-date-column {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-transform: uppercase;
  flex: 0 0 120px;
  font-family: $font-poppin-semi;
  font-weight: 300;
  color: $black;
  text-align: left;
  padding: 14px 0;

  .month {
    font-size: 12.5px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0.1em;
  }

  .date {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.42;
    margin-top: 2px;
  }

  @media only screen and (max-width: 1024px) {
    flex: 0 0 40px;
  }

  @media only screen and (max-width: 580px) {
    .month {
      font-size: 10.5px;
    }

    .date {
      font-size: 22px;
    }
  }
}

.event-info-column {
  flex: 1;
  display: flex;
  gap: 1rem;

  a {
    color: $black;
    text-decoration: none;
  }

  .event-info-text {
    flex: 1 1 65%;
    display: flex;
    flex-direction: column;
    font-family: $font-didot-ltd-roman;
    font-size: 0.9rem;
    padding: 10px 0;

    .event-daterange {
      font-family: $font-poppin-light;
      font-size: 14px;
      letter-spacing: 2px;
      margin-bottom: 0.25rem;
      text-transform: uppercase;
    }

    .event-title {
      font-family: $font-poppin-bold;
      text-transform: uppercase;
      font-weight: 700;
      font-size: 19px;
      letter-spacing: 2px;
      margin-bottom: 0.5rem;
      line-height: 1.42;
    }

    .event-body {
      font-family: $font_didot_ltd_roman;
      font-weight: 300;
      font-size: 18px;
      line-height: 22px;
      letter-spacing: -0.25px;
      color: #434343;

      @media only screen and (max-width: 992px) {
        display: none;
      }
    }
  }

  .event-info-image {
    flex: 0 0 35%;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      display: block;
      max-width: 100%;
      height: auto;
      max-height: 251px;
      object-fit: cover;
    }
  }

  @media only screen and (max-width: 992px) {
    display: flex;
    flex-direction: column;

    .event-info-text {
      order: 2;
    }

    .event-info-image {
      display: unset;
      flex: unset;
      order: 1;
      //  width: 100%;
      //  flex: unset;
      img {
        width: 100%;
        height: auto;
        max-height: 375px;
        // display: block;
        // object-fit: cover;
        // width: 100%;
        // height: 350px;
        //  max-height: unset;
      }
    }
  }
}

.event-toggle-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 28px 0;
  
  &.add-border {
    border-top: 1px solid $black;
    margin-top: 30px;
    margin-bottom: -50px;
  }

  .nav-button {
    border: none;
    font-family: $font_poppin_bold;
    text-transform: uppercase;
    background-color: $white;
    color: #5d5d5d;
    font-size: 18px;
    letter-spacing: 2px;
    transition: all 0.3s ease;

    &:hover {
      color: $black;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
      pointer-events: none;
    }

  }
  @media only screen and (max-width: 768px) {
     &.add-border {
      margin-bottom: -80px;
     }

    .nav-button {
      font-size: 12px;
    }
  }
}
</style>
